<template>
  <!-- 上余白は直前の体験セクションの pb が担うため、ここでは下余白のみ持つ (Theme/Story/Banner と統一) -->
  <section class="bg-beige1 pb-20" data-testid="reviews-section">
    <div class="flex flex-col gap-4 md:m-auto md:w-11/12 md:max-w-[1216px] md:flex-row md:items-center">
      <!-- ヘッダー（SP: 縦積み上段 / PC: 左カラム） -->
      <div class="flex flex-col gap-6 px-4 md:flex-1 md:px-0">
        <h2 class="whitespace-pre-line font-Spectral text-[32px] font-medium capitalize leading-10 tracking-[0.5px] text-light-fg-accent md:text-[36px] md:leading-[48px]">{{ HEADING }}</h2>
        <div class="font-Spectral text-base leading-6 text-light-fg-muted md:text-[17px]">
          <p class="mb-2">{{ INTRO_FIRST_LINE }}</p>
          <p>{{ INTRO_SECOND_LINE }}</p>
        </div>
      </div>

      <!-- レビューカード（SP: 横スクロール・白半透明カード / PC: 2列×3行グリッド・左縦区切り線） -->
      <ul class="slide-scrollbar flex snap-x snap-mandatory scroll-pl-4 gap-2 overflow-x-auto px-4 md:grid md:flex-1 md:snap-none md:grid-cols-2 md:gap-4 md:overflow-visible md:px-0">
        <li
          v-for="(review, index) in reviews"
          :key="`${review.country}-${review.initial}-${index}`"
          class="flex w-[260px] shrink-0 snap-start flex-col gap-2 bg-white/60 p-4 md:w-auto md:shrink md:snap-align-none md:border-l md:border-light-bd-muted md:bg-transparent md:p-0 md:pl-4"
        >
          <div class="flex items-center gap-1">
            <img class="size-6 rounded-full object-cover" loading="lazy" :alt="`${review.country} flag`" :src="review.flag" />
            <span class="font-Spectral text-xs leading-4 text-light-fg-default">{{ review.country }}</span>
          </div>
          <blockquote class="font-Spectral text-base leading-6 text-[#262626] md:text-sm md:leading-5 md:text-light-fg-default">{{ review.body }}</blockquote>
          <cite class="font-Spectral text-xs not-italic leading-4 text-light-fg-disabled">{{ review.initial }}</cite>
        </li>
      </ul>
    </div>
  </section>
</template>

<script setup lang="ts">
import australiaFlag from '@/wabunka-src/common/assets/img/flags/australia.png';
import germanyFlag from '@/wabunka-src/common/assets/img/flags/germany.png';
import indiaFlag from '@/wabunka-src/common/assets/img/flags/india.png';
import southAfricaFlag from '@/wabunka-src/common/assets/img/flags/south-africa.png';
import unitedStatesFlag from '@/wabunka-src/common/assets/img/flags/united-states.png';

const HEADING = 'Trusted by\nover 30,000 travelers';
const INTRO_FIRST_LINE = 'Travelers from around the world continue to discover the deeper beauty of Japan.';
const INTRO_SECOND_LINE = 'Over 30,000 guests from more than 100 countries have journeyed with Wabunka. Each encounter becomes part of the story we continue to write together.';

interface Review {
  country: string;
  flag: string;
  body: string;
  initial: string;
}

const reviews: Review[] = [
  {
    country: 'United States',
    flag: unitedStatesFlag,
    body: 'It was a "Zen-like immersive experience" that taught us not only the technique but also the "why," the feelings involved, and our connection to nature. I was also deeply moved that the teacher remembered me.',
    initial: 'S.S',
  },
  {
    country: 'South Africa',
    flag: southAfricaFlag,
    body: 'A dream kintsugi experience! Chako-san and the teachers were wonderful, kind, and so much fun to work with. I’m so grateful for this authentic and joyful workshop with my friends and spouse.',
    initial: 'I.K',
  },
  {
    country: 'Australia',
    flag: australiaFlag,
    body: 'It deepened my appreciation for Japanese culture and sake —its elegance, harmony, and the way tradition is carried with such respect and authenticity.',
    initial: 'H.Y',
  },
  {
    country: 'Germany',
    flag: germanyFlag,
    body: 'The chasen workshop with Tango-san was a profound cultural exchange. From the delicate bamboo crafting to sharing tea in his home, I felt the true spirit and warmth of Japanese craftsmanship.',
    initial: 'M.J',
  },
  {
    country: 'South Africa',
    flag: southAfricaFlag,
    body: 'The whole experience was special. Ayako was a delightful translator—friendly, kind, and so helpful. She answered all our questions and kindly offered to take photos, making our visit truly seamless and memorable.',
    initial: 'I.K',
  },
  {
    country: 'India',
    flag: indiaFlag,
    body: 'Our teacher and Chuko were incredibly warm, ensuring we truly enjoyed learning kintsugi’s history. Having a private class made it even more special. Their hospitality made this an unforgettable, one-of-a-kind experience!',
    initial: 'M.Y',
  },
];
</script>
